import React from "react";
import ShowimagesLoading from "./showImagesLoading";
import "./PrevviewProFilt.css";
import { elakayAutoText } from "./dataElakay";
import {
  getActualFilterValues,
  getDisplayFilterKey,
  getVariantFilterValue,
  isVariantOptionAvailable,
  normalizeVariantFilterValue,
  selectVariantForOption,
} from "./variantFilterSelection";

const findValueMetadata = (filterDefinition, value) => {
  const normalizedValue = normalizeVariantFilterValue(value);
  return filterDefinition?._id?.valueMetadata?.find((entry) =>
    normalizeVariantFilterValue(entry?.normalizedValue || entry?.value) === normalizedValue
  );
};

const resolveColorSwatchBackground = (filterDefinition, value) => {
  const metadata = findValueMetadata(filterDefinition, value);
  const colorCodes = (metadata?.colors || []).map((color) => color?.colorCode).filter(Boolean);
  if (colorCodes.length > 1) {
    const segment = 100 / colorCodes.length;
    return `linear-gradient(90deg, ${colorCodes.map((colorCode, index) =>
      `${colorCode} ${index * segment}% ${(index + 1) * segment}%`
    ).join(", ")})`;
  }
  if (metadata?.colorCode) return metadata.colorCode;
  if (colorCodes.length === 1) return colorCodes[0];
  return value;
};

const resolveFilterLabel = (filterDefinition, value) => {
  const metadata = findValueMetadata(filterDefinition, value);
  const colorLabels = (metadata?.colors || []).map((color) => color?.label).filter(Boolean);
  if (colorLabels.length) return colorLabels.join(" / ");
  return metadata?.value || value;
};

export const valuesForFilter = (filterDefinition, variants) => {
  const key = getDisplayFilterKey(filterDefinition);
  const actualValues = getActualFilterValues(variants, key);
  const configuredValues = filterDefinition?.FilterItem?.length
    ? filterDefinition.FilterItem
    : filterDefinition?._id?.FilterItem || [];

  // Same source of truth as the pre-variant component: product.filters[].FilterItem
  // is always shown in full, never trimmed down to only the values that
  // happen to have a matching filterProduct/variant record (a configured
  // value with no variant yet -- e.g. a color the seller added but hasn't
  // stocked -- used to vanish silently here). Real variant values (CJ
  // Dropshipping's auto-linked FilterItem is kept in sync with them, see
  // autoLinkCJVariantFilters) are still appended on top, exactly as before,
  // so nothing actually purchasable is ever missing either.
  const ordered = [...configuredValues];
  actualValues.forEach((value) => {
    if (!ordered.some((item) => normalizeVariantFilterValue(item) === normalizeVariantFilterValue(value))) {
      ordered.push(value);
    }
  });
  return ordered;
};

// Real, deduplicated, non-blank values only -- valuesForFilter's own output can still hold raw
// duplicates (FilterItem itself repeating a value) or blanks, so `.length` alone is not reliable
// for deciding whether a group has more than one REAL choice.
const cleanUniqueValues = (values) => {
  const seen = new Set();
  const result = [];
  (Array.isArray(values) ? values : []).forEach((value) => {
    const normalized = normalizeVariantFilterValue(value);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    result.push(value);
  });
  return result;
};

// product.filters is the sole source of truth for which filters are shown --
// a key only present in filterProduct[].filter (no matching entry in
// product.filters) is never displayed, even if real variants use it.
export const buildDisplayFilters = (configuredFilters) => {
  const filtersByKey = new Map();
  (Array.isArray(configuredFilters) ? configuredFilters : []).forEach((filterDefinition) => {
    const key = getDisplayFilterKey(filterDefinition);
    if (key && !filtersByKey.has(key)) filtersByKey.set(key, filterDefinition);
  });
  return [...filtersByKey.values()];
};

const PrevviewProFilt = ({
  infoSource,
  galeries = [],
  setImages,
  setTextFil,
  oneFilterPro = {},
  restAllFilterPro = [],
  setOneFilterPro,
  setALLFilterPro,
  allFilterPro = [],
  filter = [],
  setNewAP,
  imageTextDiv,
  del = false,
}) => {
  const variants = (Array.isArray(restAllFilterPro) && restAllFilterPro.length
    ? restAllFilterPro
    : Array.isArray(allFilterPro) ? allFilterPro : []).filter(Boolean);
  // A group with zero or exactly one REAL (deduplicated, non-blank) value is never shown as a
  // selector -- the Seller/buyer has nothing to choose. The underlying variant match is untouched
  // (oneFilterPro already carries that single value internally, see loadQueryFil in
  // productPreview.js): this only hides the UI group, it never removes the value from the product.
  const displayFilters = buildDisplayFilters(filter).filter((entry) =>
    entry?.formValueFil !== "None" && getDisplayFilterKey(entry) &&
    cleanUniqueValues(valuesForFilter(entry, variants)).length > 1
  );
  const currentSelection = oneFilterPro?.filter || {};

  const updateImagesForVariant = (variant) => {
    const directImages = variant?.images || variant?.Images;
    if (Array.isArray(directImages) && directImages.length) {
      setImages?.(directImages);
      return;
    }
    const selectedValues = Object.values(variant?.filter || {}).map(normalizeVariantFilterValue);
    const gallery = (Array.isArray(galeries) ? galeries : []).find((entry) =>
      selectedValues.includes(normalizeVariantFilterValue(entry?.name)) && Array.isArray(entry?.images) && entry.images.length
    );
    if (gallery) setImages?.(gallery.images);
  };

  const selectOption = (filterDefinition, value) => {
    const key = getDisplayFilterKey(filterDefinition);
    const selectedVariant = selectVariantForOption(variants, currentSelection, key, value);
    if (!selectedVariant) return;
    setOneFilterPro?.(selectedVariant);
    setALLFilterPro?.(variants);
    setNewAP?.(selectedVariant, variants);
    updateImagesForVariant(selectedVariant);
  };

  const optionState = (filterDefinition, value) => {
    const key = getDisplayFilterKey(filterDefinition);
    // Same rationale as valuesForFilter: without a backing variant record for
    // this filter, there is no stock combo to gate on -- keep every
    // configured value clickable (pre-variant behavior). CJ Dropshipping
    // products always have variant records, so isVariantOptionAvailable's
    // quantity check still applies to them exactly as before.
    const hasVariantData = getActualFilterValues(variants, key).length > 0;
    return {
      available: del || !hasVariantData || isVariantOptionAvailable(variants, key, value),
      selected: normalizeVariantFilterValue(getVariantFilterValue(oneFilterPro, key)) ===
        normalizeVariantFilterValue(value),
    };
  };

  const selectImageOption = (filterDefinition, label, image) => {
    if (del) {
      setImages?.(image);
      setTextFil?.(label);
      if (Array.isArray(imageTextDiv)) imageTextDiv.splice(0, 2, label, image);
      return;
    }
    selectOption(filterDefinition, label);
  };

  const renderSwatches = (filterDefinition) => {
    const key = getDisplayFilterKey(filterDefinition);
    const isColor = key === "color";
    const isSquare = filterDefinition.formValueFil === "Square" || filterDefinition.formValueFil === "Color Square";
    return (
      <div className="filter-swatches">
        {valuesForFilter(filterDefinition, variants).map((value) => {
          const { available, selected } = optionState(filterDefinition, value);
          const visibleLabel = resolveFilterLabel(filterDefinition, value);
          return (
            <button
              key={String(value)}
              type="button"
              disabled={!available}
              className={`filter-swatch ${isSquare ? "square" : "circle"} ${available ? "available" : "unavailable"} ${selected ? "selected" : ""}`}
              style={isColor ? { background: resolveColorSwatchBackground(filterDefinition, value) } : undefined}
              onClick={() => selectOption(filterDefinition, value)}
              aria-pressed={selected}
              aria-label={elakayAutoText("h_a9ef046ebdfd2f", "Select {value0}", { value0: visibleLabel })}
              title={visibleLabel}
            >
              {(filterDefinition.textRead || !isColor) && <span className="swatch-label">{visibleLabel}</span>}
            </button>
          );
        })}
      </div>
    );
  };

  const renderSelect = (filterDefinition) => {
    const key = getDisplayFilterKey(filterDefinition);
    const currentValue = getVariantFilterValue(oneFilterPro, key) || "";
    return (
      <select
        className="filter-select"
        value={currentValue}
        onChange={(event) => selectOption(filterDefinition, event.target.value)}
      >
        <option value="" disabled>{infoSource?.language?.(infoSource, "label", "selWaNeed") || elakayAutoText("h_08d7aa35da9a4d", "Select an option")}</option>
        {valuesForFilter(filterDefinition, variants).map((value) => {
          const { available } = optionState(filterDefinition, value);
          return <option key={String(value)} value={value} disabled={!available}>{resolveFilterLabel(filterDefinition, value)}</option>;
        })}
      </select>
    );
  };

  const renderImages = (filterDefinition) => {
    const actualValues = new Set(valuesForFilter(filterDefinition, variants).map(normalizeVariantFilterValue));
    return (
      <div className="filter-image-grid">
        {(Array.isArray(filterDefinition.Images) ? filterDefinition.Images : [])
          .filter((item) => del || actualValues.has(normalizeVariantFilterValue(item?.[0])))
          .map(([label, image]) => {
            const { available, selected } = optionState(filterDefinition, label);
            return (
              <button
                key={String(label)}
                type="button"
                disabled={!available}
                className={`filter-image-item ${available ? "available" : "unavailable"} ${selected ? "selected" : ""}`}
                onClick={() => selectImageOption(filterDefinition, label, image)}
                aria-pressed={selected}
              >
                <div className="image-wrapper">
                  <ShowimagesLoading src={image} alt={label} />
                  {filterDefinition.textRead && <div className="image-label">{resolveFilterLabel(filterDefinition, label)}</div>}
                </div>
              </button>
            );
          })}
      </div>
    );
  };

  return (
    <div className="filter-selector-modern">
      {displayFilters.map((filterDefinition) => {
        const formValue = filterDefinition.formValueFil;
        const hasConfiguredImages = Array.isArray(filterDefinition.Images) && filterDefinition.Images.length > 0;
        const filterKey = getDisplayFilterKey(filterDefinition);
        // Only the VARIANT group gets the capped, scrollable container (ticket part 10) -- a
        // product with many long combinations (e.g. "Stripes 110cm") no longer pushes the whole
        // Product Preview far down the page. Other groups (Color/Size/...) render at full height.
        const isVariantGroup = filterKey === "variant";
        return (
          <div key={filterKey} className="filter-group">
            <label className="filter-label">{filterDefinition?._id?.name || filterDefinition?.name}</label>
            <div className={`filter-options${isVariantGroup ? " filter-options--variant" : ""}`}>
              {(formValue === "Images" || formValue === "product") && hasConfiguredImages
                ? renderImages(filterDefinition)
                : filterDefinition.formInput ? renderSwatches(filterDefinition) : renderSelect(filterDefinition)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PrevviewProFilt;
